import {
	Box,
	createStyles,
	keyframes,
	Stack,
	UnstyledButton,
} from '@mantine/core';
import { Text } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import type { AiChatStore } from '../store';
import { shouldShowPromptSuggestions } from '../utils';

const useStyles = createStyles((theme) => {
	// Spring-like animation for container sliding up and fading in
	const revealContainer = keyframes({
		'0%': {
			transform: 'translate3d(0, 200px, 0)',
			opacity: 0,
		},
		'18.1%': {
			transform: 'translate3d(0, 200px, 0)',
			opacity: 0,
		},
		'35.0%': {
			transform: 'translate3d(0, -5.51px, 0)',
			opacity: 1,
		},
		'58.0%': {
			transform: 'translate3d(0, 0.81px, 0)',
			opacity: 1,
		},
		'81.0%': {
			transform: 'translate3d(0, -0.06px, 0)',
			opacity: 1,
		},
		'100%': {
			transform: 'translate3d(0, 0, 0)',
			opacity: 1,
		},
	});

	// Spring-like animation for gap changing from 6x to 1x
	const revealGap = keyframes({
		'0%': {
			gap: `calc(${theme.spacing.xs} * 10)`,
		},
		'18.1%': {
			gap: `calc(${theme.spacing.xs} * 10)`,
		},
		'35.0%': {
			gap: `calc(${theme.spacing.xs} * 0.9)`,
		},
		'58.0%': {
			gap: `calc(${theme.spacing.xs} * 1.01)`,
		},
		'81.0%': {
			gap: `calc(${theme.spacing.xs} * 0.999)`,
		},
		'100%': {
			gap: theme.spacing.xs,
		},
	});

	return {
		suggestionChip: {
			backgroundColor: theme.other.getColor('fill/secondary/default'),
			borderRadius: theme.radius.lg,
			padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
			cursor: 'pointer',
			'&:hover': {
				backgroundColor: theme.other.getColor('fill/secondary/hover'),
			},
		},
		suggestionText: {
			color: theme.other.getColor('text/primary/default'),
			fontSize: theme.fontSizes.sm,
			fontWeight: 400,
			lineHeight: 1.4,
		},
		container: {
			// Position absolutely so it doesn't affect AIChatEmpty layout
			position: 'absolute',
			bottom: '100%',
			left: 0,
			right: 0,
			// Set initial state to match animation start to prevent layout shifts
			transform: 'translate3d(0, 200px, 0)',
			opacity: 0,
			// Matt wanted a soft, springy effect here:
			// https://www.figma.com/proto/Ag4DEWtazWwaJvx2DHjKCe/Secoda-AI?page-id=5970%3A54677&node-id=6146-25752&viewport=3327%2C-162%2C1.24&t=qXD0dSFmywZpggje-1
			// eslint-disable-next-line no-custom-animation/no-custom-animation
			animation: `${revealContainer} 2.2s cubic-bezier(0.445, 0.050, 0.550, 0.950)`,
			// Ensure animation starts from initial state and stays at final state
			animationFillMode: 'both',
			// must be below AIPromptInput
			zIndex: 1,
		},
		contentWrapper: {
			overflow: 'hidden',
			position: 'relative',
			zIndex: 2,
			// Set initial gap to match animation start to prevent layout shifts
			gap: `calc(${theme.spacing.xs} * 10)`,
			// eslint-disable-next-line no-custom-animation/no-custom-animation
			animation: `${revealGap} 2.2s cubic-bezier(0.445, 0.050, 0.550, 0.950)`,
			// Ensure animation starts from initial state and stays at final state
			animationFillMode: 'both',
		},
		overlay: {
			// Static overlay to block content below the input
			// Positioned relative to parent (AiViewportWrapper), not the animated container
			position: 'absolute',
			// Position at the top of parent (which is where input's top is)
			// Since container uses bottom: 100%, its bottom aligns with parent's top
			top: 0,
			left: 0,
			right: 0,
			height: '300px',
			// Same background as input to seamlessly blend
			backgroundColor: theme.other.getColor('surface/primary/default'),
			// backgroundColor: 'red',
			// Above suggestions (z-index 2) so it covers them while the suggestions are below the input
			zIndex: 2,
			pointerEvents: 'none', // Don't block interactions
		},
		loadingChip: {
			height: 32,
			width: 120,
			borderRadius: theme.radius.lg,
			backgroundColor: theme.other.getColor('fill/secondary/default'),
			transform: 'scaleY(0)',
			opacity: 0,
			transformOrigin: 'top',
		},
	};
});

interface PromptSuggestionsProps {
	store: AiChatStore;
	onSelect: (prompt: string) => void;
	suggestions: string[];
	isLoading: boolean;
}

function PromptSuggestionsInternal({
	store,
	onSelect,
	suggestions,
	isLoading,
}: PromptSuggestionsProps) {
	const { classes } = useStyles();
	// Determine if we should show suggestions based on context
	// For post-response suggestions, we always show if suggestions exist
	const shouldShowSuggestions = shouldShowPromptSuggestions(
		store.startingPointEntityIdentifier
	);

	// Only show suggestions if context allows
	if (!shouldShowSuggestions) {
		return null;
	}

	if (!suggestions.length || isLoading) {
		return null;
	}

	return (
		<>
			{/* Static overlay - positioned relative to parent, not moving with animation */}
			<Box className={classes.overlay} />
			{/* Animated suggestions container */}
			<Box className={classes.container}>
				<Stack className={classes.contentWrapper} p="md">
					{suggestions.map((suggestion, index) => (
						<UnstyledButton
							key={`prompt-suggestion-${suggestion}`}
							className={classes.suggestionChip}
							onClick={() => onSelect(suggestion)}
							data-testid={`prompt-suggestion-${index}`}
						>
							<Text className={classes.suggestionText}>{suggestion}</Text>
						</UnstyledButton>
					))}
				</Stack>
			</Box>
		</>
	);
}

export const PromptSuggestions = observer(PromptSuggestionsInternal);
